import type { SftpUserBehaviors } from "../access/SftpUser/behaviors";
import type { SshUserBehaviors } from "../access/SshUser/behaviors";
import type { AppBehaviors } from "../app/App/behaviors";
import type { CityBehaviors } from "../fyndr/City/behavior";
import type { FinderProfileBehaviors } from "../fyndr/FinderProfile/behaviors";
import type { FinderProfileRequestBehaviors } from "../fyndr/FinderProfileRequest/behaviors";
import type { LeadBehaviors } from "../fyndr/Lead/behavior";
import type { UnlockedLeadBehaviors } from "../fyndr/UnlockedLead/behavior";
import type { AppInstallationBehaviors } from "../app/AppInstallation/behaviors";
import type { AppVersionBehaviors } from "../app/AppVersion/behaviors";
import type { SystemSoftwareBehaviors } from "../app/SystemSoftware/behaviors";
import type { SystemSoftwareVersionBehaviors } from "../app/SystemSoftwareVersion/behaviors";
import type { ArticleBehaviors } from "../article/Article/behaviors";
import type { MfaBehaviors } from "../auth/Mfa/behaviors";
import type { BackupBehaviors } from "../backup/Backup/behaviors";
import type { BackupScheduleBehaviors } from "../backup/BackupSchedule/behaviors";
import type { ContainerBehaviors } from "../container/Container/behaviors";
import type { RegistryBehaviors } from "../container/Registry/behaviors";
import type { VolumeBehaviors } from "../container/Volume/behaviors";
import type { ContractBehaviors } from "../contract/Contract/behaviors";
import type { ContractItemBehaviors } from "../contract/ContractItem/behaviors";
import type { ConversationBehaviors } from "../conversation/Conversation/behaviors";
import type { CronjobBehaviors } from "../cronjob/Cronjob/behaviors";
import type { CronjobExecutionBehaviors } from "../cronjob/CronjobExecution/behaviors";
import type { CustomerBehaviors } from "../customer/Customer/behaviors";
import type { CustomerInviteBehaviors } from "../customer/CustomerInvite/behaviors";
import type { CustomerMembershipBehaviors } from "../customer/CustomerMembership/behaviors";
import type { InvoiceSettingsBehaviors } from "../customer/InvoiceSettings/behaviors";
import type { WalletBehaviors } from "../customer/Wallet/behaviors";
import type { MySqlBehaviors } from "../database/MySql/behaviors";
import type { MySqlUserBehaviors } from "../database/MySqlUser/behaviors";
import type { RedisBehaviors } from "../database/Redis/behaviors";
import type { CertificateBehaviors } from "../domain/Certificate/behaviors";
import type { CertificateRequestBehaviors } from "../domain/CertificateRequest/behaviors";
import type { DnsZoneBehaviors } from "../domain/DnsZone/behaviors";
import type { DomainBehaviors } from "../domain/Domain/behaviors";
import type { IngressBehaviors } from "../domain/Ingress/behaviors";
import type { TldBehaviors } from "../domain/Tld/behaviors";
import type { FileBehaviors } from "../file/File/behaviors";
import type { InvoiceBehaviors } from "../invoice/Invoice/behaviors";
import type { DeliveryBoxBehaviors } from "../mail/DeliveryBox/behaviors";
import type { MailAddressBehaviors } from "../mail/MailAddress/behaviors";
import type { MailSettingsBehaviors } from "../mail/MailSettings/behaviors";
import type { ContributorBehaviors } from "../marketplace/Contributor/behaviors";
import type { ContributorExtensionBehaviors } from "../marketplace/ContributorExtension/behaviors";
import type { ExtensionBehaviors } from "../marketplace/Extension/behaviors";
import type { ExtensionInstanceBehaviors } from "../marketplace/ExtensionInstance/behaviors";
import type { StorageMetricsBehaviors } from "../monitoring/StorageMetrics/behaviors";
import type { NewsletterBehaviors } from "../newsletter/Newsletter/behaviors";
import type { NotificationBehaviors } from "../notifications/Notifications/behaviors";
import type { OrderBehaviors } from "../order/Order/behaviors";
import type { ProjectBehaviors } from "../project/Project/behaviors";
import type { ProjectInviteBehaviors } from "../project/ProjectInvite/behaviors";
import type { ProjectMembershipBehaviors } from "../project/ProjectMembership/behaviors";
import type { ServerBehaviors } from "../server/Server/behaviors";
import type { ApiTokenBehaviors } from "../user/ApiToken/behaviors";
import type { FeedbackBehaviors } from "../user/Feedback/behaviors";
import type { SessionBehaviors } from "../user/Session/behaviors";
import type { SshKeyBehaviors } from "../user/SshKey/behaviors";
import type { SupportCodeBehaviors } from "../user/SupportCode/behaviors";
import type { UserBehaviors } from "../user/User/behaviors";

interface Config {
  defaultPaginationLimit: number;
  behaviors: {
    apiToken: ApiTokenBehaviors;
    app: AppBehaviors;
    appInstallation: AppInstallationBehaviors;
    appVersion: AppVersionBehaviors;
    article: ArticleBehaviors;
    backup: BackupBehaviors;
    backupSchedule: BackupScheduleBehaviors;
    certificate: CertificateBehaviors;
    certificateRequest: CertificateRequestBehaviors;
    container: ContainerBehaviors;
    contract: ContractBehaviors;
    contractItem: ContractItemBehaviors;
    contributor: ContributorBehaviors;
    contributorExtension: ContributorExtensionBehaviors;
    conversation: ConversationBehaviors;
    cronjob: CronjobBehaviors;
    cronjobExecution: CronjobExecutionBehaviors;
    customer: CustomerBehaviors;
    customerInvite: CustomerInviteBehaviors;
    customerMembership: CustomerMembershipBehaviors;
    deliveryBox: DeliveryBoxBehaviors;
    dnsZone: DnsZoneBehaviors;
    domain: DomainBehaviors;
    extension: ExtensionBehaviors;
    extensionInstance: ExtensionInstanceBehaviors;
    feedback: FeedbackBehaviors;
    file: FileBehaviors;
    ingress: IngressBehaviors;
    invoice: InvoiceBehaviors;
    invoiceSettings: InvoiceSettingsBehaviors;
    mailAddress: MailAddressBehaviors;
    mailSettings: MailSettingsBehaviors;
    mfa: MfaBehaviors;
    mySql: MySqlBehaviors;
    mySqlUser: MySqlUserBehaviors;
    newsletter: NewsletterBehaviors;
    notification: NotificationBehaviors;
    order: OrderBehaviors;
    project: ProjectBehaviors;
    projectInvite: ProjectInviteBehaviors;
    projectMembership: ProjectMembershipBehaviors;
    redis: RedisBehaviors;
    registry: RegistryBehaviors;
    server: ServerBehaviors;
    session: SessionBehaviors;
    sftpUser: SftpUserBehaviors;
    sshKey: SshKeyBehaviors;
    sshUser: SshUserBehaviors;
    storageMetrics: StorageMetricsBehaviors;
    supportCode: SupportCodeBehaviors;
    systemSoftware: SystemSoftwareBehaviors;
    systemSoftwareVersion: SystemSoftwareVersionBehaviors;
    tld: TldBehaviors;
    user: UserBehaviors;
    volume: VolumeBehaviors;
    wallet: WalletBehaviors;
    finderProfile: FinderProfileBehaviors;
    finderProfileRequest: FinderProfileRequestBehaviors;
    lead: LeadBehaviors;
    unlockedLead: UnlockedLeadBehaviors;
    city: CityBehaviors;
  };
}

export const config: Config = {
  defaultPaginationLimit: 50,
  behaviors: {
    apiToken: undefined as unknown as ApiTokenBehaviors,
    app: undefined as unknown as AppBehaviors,
    appInstallation: undefined as unknown as AppInstallationBehaviors,
    appVersion: undefined as unknown as AppVersionBehaviors,
    article: undefined as unknown as ArticleBehaviors,
    backup: undefined as unknown as BackupBehaviors,
    backupSchedule: undefined as unknown as BackupScheduleBehaviors,
    certificate: undefined as unknown as CertificateBehaviors,
    certificateRequest: undefined as unknown as CertificateRequestBehaviors,
    container: undefined as unknown as ContainerBehaviors,
    contract: undefined as unknown as ContractBehaviors,
    contractItem: undefined as unknown as ContractItemBehaviors,
    contributor: undefined as unknown as ContributorBehaviors,
    contributorExtension: undefined as unknown as ContributorExtensionBehaviors,
    conversation: undefined as unknown as ConversationBehaviors,
    cronjob: undefined as unknown as CronjobBehaviors,
    cronjobExecution: undefined as unknown as CronjobExecutionBehaviors,
    customer: undefined as unknown as CustomerBehaviors,
    customerInvite: undefined as unknown as CustomerInviteBehaviors,
    customerMembership: undefined as unknown as CustomerMembershipBehaviors,
    deliveryBox: undefined as unknown as DeliveryBoxBehaviors,
    dnsZone: undefined as unknown as DnsZoneBehaviors,
    domain: undefined as unknown as DomainBehaviors,
    extension: undefined as unknown as ExtensionBehaviors,
    extensionInstance: undefined as unknown as ExtensionInstanceBehaviors,
    feedback: undefined as unknown as FeedbackBehaviors,
    file: undefined as unknown as FileBehaviors,
    ingress: undefined as unknown as IngressBehaviors,
    invoice: undefined as unknown as InvoiceBehaviors,
    invoiceSettings: undefined as unknown as InvoiceSettingsBehaviors,
    mailAddress: undefined as unknown as MailAddressBehaviors,
    mailSettings: undefined as unknown as MailSettingsBehaviors,
    mfa: undefined as unknown as MfaBehaviors,
    mySql: undefined as unknown as MySqlBehaviors,
    mySqlUser: undefined as unknown as MySqlUserBehaviors,
    newsletter: undefined as unknown as NewsletterBehaviors,
    notification: undefined as unknown as NotificationBehaviors,
    order: undefined as unknown as OrderBehaviors,
    project: undefined as unknown as ProjectBehaviors,
    projectInvite: undefined as unknown as ProjectInviteBehaviors,
    projectMembership: undefined as unknown as ProjectMembershipBehaviors,
    redis: undefined as unknown as RedisBehaviors,
    registry: undefined as unknown as RegistryBehaviors,
    server: undefined as unknown as ServerBehaviors,
    session: undefined as unknown as SessionBehaviors,
    sftpUser: undefined as unknown as SftpUserBehaviors,
    sshKey: undefined as unknown as SshKeyBehaviors,
    sshUser: undefined as unknown as SshUserBehaviors,
    storageMetrics: undefined as unknown as StorageMetricsBehaviors,
    supportCode: undefined as unknown as SupportCodeBehaviors,
    systemSoftware: undefined as unknown as SystemSoftwareBehaviors,
    systemSoftwareVersion:
      undefined as unknown as SystemSoftwareVersionBehaviors,
    tld: undefined as unknown as TldBehaviors,
    user: undefined as unknown as UserBehaviors,
    volume: undefined as unknown as VolumeBehaviors,
    wallet: undefined as unknown as WalletBehaviors,
    finderProfile: undefined as unknown as FinderProfileBehaviors,
    finderProfileRequest: undefined as unknown as FinderProfileRequestBehaviors,
    lead: undefined as unknown as LeadBehaviors,
    unlockedLead: undefined as unknown as UnlockedLeadBehaviors,
    city: undefined as unknown as CityBehaviors,
  },
};
